import {
  ACCOUNT_TYPES,
  ASSETS_GROUPS,
  type AccountInfoDto,
} from '@/entities/account';
import { sortByDate } from '@/shared/lib/sorting';
import type {
  AccountsRestsBaseResponseDto,
  AccountRest,
  AccountsMarginRestsBaseResponseDto,
  AccountMarginRest,
  AssetRests,
  AccountsByType,
  MarginAssetRests,
  RegularRests,
  AccountType,
  MarginAccount,
} from './types';

const isEwalletAccountType = (account: AccountInfoDto) => {
  return account.accTypeName === ACCOUNT_TYPES.Main;
};

const isInvestAccountType = (
  account: AccountInfoDto,
  tradingInvestCodes: Array<string>
) => {
  return (
    account.accTypeName === ACCOUNT_TYPES.Trading &&
    tradingInvestCodes.includes(account.tradingProductCode || '')
  );
};

const isSpotAccountType = (
  account: AccountInfoDto,
  tradingSpotCodes: Array<string>
) => {
  return (
    account.accTypeName === ACCOUNT_TYPES.Trading &&
    tradingSpotCodes.includes(account.tradingProductCode || '')
  );
};

const isLeverageAccountType = (
  account: AccountInfoDto,
  tradingLeverageCodes: Array<string>
) => {
  return (
    account.accTypeName === ACCOUNT_TYPES.Trading &&
    tradingLeverageCodes.includes(account.tradingProductCode || '')
  );
};

const isEarnAccountType = (
  account: AccountInfoDto,
  tradingEarnCodes: Array<string>
) => {
  return (
    account.accTypeName === ACCOUNT_TYPES.Trading &&
    tradingEarnCodes.includes(account.tradingProductCode || '')
  );
};

export const splitAccountsByType = (
  accounts: Array<AccountInfoDto>,
  tradingSpotCodes: Array<string>,
  tradingInvestCodes: Array<string>,
  tradingLeverageCodes: Array<string>,
  tradingEarnCodes: Array<string>
) => {
  const accountsByType: AccountsByType = {
    ewallet: [],
    invest: [],
    spot: [],
    leverage: [],
    earn: [],
  };

  accounts.forEach(account => {
    if (isEwalletAccountType(account)) {
      accountsByType.ewallet.push(account);
    }
    if (isInvestAccountType(account, tradingInvestCodes)) {
      accountsByType.invest.push(account);
    }
    if (isSpotAccountType(account, tradingSpotCodes)) {
      accountsByType.spot.push(account);
    }
    if (isLeverageAccountType(account, tradingLeverageCodes)) {
      accountsByType.leverage.push(account);
    }
    if (isEarnAccountType(account, tradingEarnCodes)) {
      accountsByType.earn.push(account);
    }
  });

  return accountsByType;
};

export const getAccountTypeCreatedDate = (accounts: Array<AccountInfoDto>) => {
  if (accounts.length === 0) {
    return '';
  }
  return accounts.sort((a, b) => sortByDate('asc')(a.created, b.created))[0]
    .created;
};

export const getRegularAccountsFromRests = (
  accounts: Array<AccountsRestsBaseResponseDto>,
  accountsInfo: Array<AccountInfoDto>
) => {
  return accounts.reduce((acc, account) => {
    return [
      ...acc,
      ...account.accounts.map(account => {
        const accountInfo = accountsInfo.find(
          acc => acc.accNum === account.accNum
        );
        return {
          ...account,
          tradingAccKey: accountInfo?.tradingAccKey,
          leverage: accountInfo?.leverage,
        };
      }),
    ];
  }, [] as Array<AccountRest>);
};

export const getMarginAccountsFromRests = (
  accounts: AccountsMarginRestsBaseResponseDto | null
) => {
  if (!accounts) {
    return [];
  }
  return accounts.accounts.reduce((acc, account) => {
    return [...acc, account];
  }, [] as Array<AccountMarginRest>);
};

export const getMarginAccountsFromMarginResponse = (
  response: AccountsMarginRestsBaseResponseDto | null,
  accountsInfo: Array<AccountInfoDto>
): Array<MarginAccount> => {
  if (!response) {
    return [];
  }
  return response.accounts.map(account => ({
    accNum: account.accNum,
    tradingAccKey: account.tradingAccKey,
    tradingProductCode: account.tradingProductCode,
    leverage: accountsInfo.find(acc => acc.accNum === account.accNum)?.leverage,
    assets: account.balanceAsset
      ? [
          {
            ...account.balanceAsset,
            equity: account.equity,
            baseEquity: account.baseEquity,
            baseEquity00h: account.baseEquity00h,
            margin: account.margin,
            baseMargin: account.baseMargin,
          },
        ]
      : [],
  }));
};

export const getAssetsRests = (account: {
  accNum: string;
  assets: Array<AssetRests>;
}) => {
  return account.assets.reduce(
    (acc, asset) => ({
      ...acc,
      baseAvailable: acc.baseAvailable + (asset.baseAvailable || 0),
      baseReserved: acc.baseReserved + (asset.baseReserved || 0),
      baseAvailable00h: acc.baseAvailable00h + (asset.baseAvailable00h || 0),
      baseReserved00h: acc.baseReserved00h + (asset.baseReserved00h || 0),
    }),
    {
      baseAvailable: 0,
      baseReserved: 0,
      baseAvailable00h: 0,
      baseReserved00h: 0,
    }
  );
};

export const getRegularAccountsAssets = (
  accounts: Array<AccountsRestsBaseResponseDto>
) => {
  return accounts.reduce((acc, account) => {
    const assets = account.accounts.reduce((acc, account) => {
      return [...acc, ...account.assets];
    }, [] as Array<AssetRests>);

    return [...acc, ...assets];
  }, [] as Array<AssetRests>);
};

export const getMarginAccountsAssets = (
  accounts: AccountsMarginRestsBaseResponseDto['accounts']
) => {
  return accounts.reduce((acc, account) => {
    return account.balanceAsset
      ? [
          ...acc,
          {
            ...account.balanceAsset,
            equity: account.equity,
            baseEquity: account.baseEquity,
            baseEquity00h: account.baseEquity00h,
            margin: account.margin,
            baseMargin: account.baseMargin,
          },
        ]
      : acc;
  }, [] as Array<MarginAssetRests>);
};

export const getRegularAccountsRests = (
  accounts: Array<{
    accNum: string;
    assets: Array<AssetRests>;
  }>
) => {
  const accountsRests = accounts.map(account => {
    return getAssetsRests(account);
  });

  return accountsRests.reduce(
    (acc, account) => ({
      ...acc,
      baseAvailable: acc.baseAvailable + account.baseAvailable,
      baseReserved: acc.baseReserved + account.baseReserved,
      baseAvailable00h: acc.baseAvailable00h + account.baseAvailable00h,
      baseReserved00h: acc.baseReserved00h + account.baseReserved00h,
    }),
    {
      baseAvailable: 0,
      baseReserved: 0,
      baseAvailable00h: 0,
      baseReserved00h: 0,
    }
  );
};

export const getMarginAccountsRests = (
  marginAccounts: Array<MarginAccount>
) => {
  return marginAccounts.reduce(
    (acc, account) => ({
      ...acc,
      baseEquity: acc.baseEquity + (account.assets[0]?.baseEquity || 0),
      baseEquity00h:
        acc.baseEquity00h + (account.assets[0]?.baseEquity00h || 0),
    }),
    { baseEquity: 0, baseEquity00h: 0 }
  );
};

export const getDeltaPercent = (
  balance: number,
  midnightBalance: number
): number | null => {
  const deltaPercent = (balance / midnightBalance - 1) * 100;

  if (Number.isNaN(deltaPercent)) {
    return 0;
  }

  return Number.isFinite(deltaPercent) ? deltaPercent : null;
};

export const getAccountBalanceWithDeltas = (
  type: AccountType,
  accountsRests:
    | RegularRests
    | Pick<MarginAssetRests, 'baseEquity' | 'baseEquity00h'>
) => {
  let balance = 0;
  let midnightBalance = 0;

  if (type === 'leverage' || type === 'invest' || type === 'earn') {
    const { baseEquity, baseEquity00h } = accountsRests as MarginAssetRests;

    balance = baseEquity || 0;
    midnightBalance = baseEquity00h || 0;
  } else {
    const { baseAvailable, baseReserved, baseAvailable00h, baseReserved00h } =
      accountsRests as RegularRests;

    balance = baseAvailable + baseReserved;
    midnightBalance = baseAvailable00h + baseReserved00h;
  }

  const deltaPercent = getDeltaPercent(balance, midnightBalance);

  return { balance, midnightBalance, deltaPercent };
};

export const getGeneralBalance = (
  ewalletBalance: number,
  spotBalance: number,
  leverageBalance: number,
  investBalance: number,
  earnBalance: number
) => {
  return (
    ewalletBalance + spotBalance + leverageBalance + investBalance + earnBalance
  );
};

export const getGeneralMidnightBalance = (
  ewalletMidnightBalance: number,
  spotMidnightBalance: number,
  investMidnightBalance: number,
  leverageMidnightBalance: number,
  earnMidnightBalance: number
) => {
  return (
    ewalletMidnightBalance +
    spotMidnightBalance +
    investMidnightBalance +
    leverageMidnightBalance +
    earnMidnightBalance
  );
};

export const sortAssets = (assets: Array<AssetRests | MarginAssetRests>) => {
  return assets.sort((a, b) => {
    const aIsFiat = a.group === ASSETS_GROUPS.fiat;
    const bIsFiat = b.group === ASSETS_GROUPS.fiat;

    if (aIsFiat && !bIsFiat) {
      return -1;
    }
    if (!aIsFiat && bIsFiat) {
      return 1;
    }

    const aBalance =
      'baseEquity' in a
        ? a.baseEquity || 0
        : (a.baseAvailable || 0) + (a.baseReserved || 0);

    const bBalance =
      'baseEquity' in b
        ? b.baseEquity || 0
        : (b.baseAvailable || 0) + (b.baseReserved || 0);

    return bBalance - aBalance;
  });
};
